// THE grid Filters panel: Figma Component 73 (A792, then Aaron's ruling 2026-10-13: "Component 73
// everywhere").
//
// Every MUI X grid's FILTERS popover draws from this ONE file: the list grid (AeronetDataGrid,
// Pro 6.9), the detail-tab grid (AeronetDataGridPremium, Premium 7.1), Inventory Demand's grid,
// the raw module grids (`slots.filterPanel: CustomFilterPanelPremium` / `CustomFilterPanel`) and
// a grid's column-menu "Filter" item, which opens the same slot. The Tools list's own popover
// (PR #45 ModuleListFilters, the first Component 73 build) converged into it.
//
//   ┌──────────────────────────────────────────── Show Quick Filters ◯ ┐
//   │ Default Filters                                                   │
//   │ ───────────────────────────────────────────────────────────────── │
//   │ [Status            ▾] [Person responsible ▾] [Class ▾]            │
//   │ Custom Filters                                                    │
//   │ ───────────────────────────────────────────────────────────────── │
//   │ [Column ▾] [Operator ▾] [Value      ]                          🗑 │
//   │ + Add Criteria                                       🗑 Remove All │
//   └───────────────────────────────────────────────────────────────────┘
//
// A grid with no Default (status) options shows Custom Filters only — no fabricated Default
// section — and the Show Quick Filters switch only where there are options to show (ruling 4).
//
// Beside Status the Default section may hold more fields (`defaultFilters`: production's Tool
// Requests "Requested By" and "Tool Type", gitlab/develop ToolRequestFilterFields.tsx), each an
// "is" pick on one grid column: a tenant-option select, or the caller's own field (`renderField`,
// e.g. UserFilterAutocomplete). A field with no options and no renderField is not drawn. The
// field must be a grid column with an "is" operator (MUI drops a filter item whose column is
// missing); the Custom section never offers it.
//
// Behaviour, everywhere: AND only (a server list's `filter` param is AND-only, and Component 73
// draws no logic column), columns A–Z (`columnsSort: "asc"`, ruling 3), small outlined inputs.
// Edits go through the grid's own filter API with MUI's own reasons, so a list grid stages them
// for Display → Save exactly as before (DataGrid.tsx onFilterChange, filterEditReasons.ts);
// nothing here writes.
//
// One body for both grid majors: Pro 6.9 and Premium 7.1 have separate contexts, so each wrapper
// (CustomFilterPanel / CustomFilterPanelPremium) reads its own grid's apiRef, model and filter form
// and hands them in. Callers supply columns (and per-column `type` / `valueOptions` /
// `filterOperators`) and, optionally, `statusFilter` and `defaultFilters`; never panel styling.
//
// @figma Component 73 · Filter Panel, one default filter + custom criteria
// https://www.figma.com/design/HDBjSeAYxCpDOHrBaib9ik/Tools?node-id=26-1383
// Geometry is PR #45's verified Tools build. Figma property → prop: Default section →
// `statusFilter.options` (absent: no section); Show Quick Filters → `statusFilter.quickFiltersKey`.
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { DROPDOWN_MENU_BG, DROPDOWN_MENU_SHADOW } from "../dropdownMenuStyles";
import { useShowQuickFilters } from "../QuickFilterBar";

/** A tenant status code and its label (the tenant's config supplies both). */
export interface ListStatusOption {
  value: number | string;
  label: string;
}

/**
 * The Default Filters section, and the Quick Filters row that filters the same field.
 * `multiple` (Inventory Demand) filters "is any of"; otherwise "is" one status (Tools).
 * The field is the grid's status column unless `field` names another (AeronetDataGrid
 * `quickFilterField`: Due Date Control's Department chips, Aaron 2026-10-21).
 */
export interface StatusFilterConfig {
  options: ListStatusOption[];
  multiple?: boolean;
  /** The grid column the select and the chips filter; default "status". */
  field?: string;
  /** The select's placeholder and accessible name; default "Status". */
  label?: string;
  /** Where the viewer's Show Quick Filters choice is kept (useShowQuickFilters). */
  quickFiltersKey?: string;
  /** Whether the Quick Filters row shows before the viewer has chosen. */
  quickFiltersDefault?: boolean;
}

/**
 * One more field in the Default Filters section (after Status): an "is" pick on one grid column.
 * Production's `DesignDefaultFilter` (gitlab/develop DesignFilterPanel.tsx), on this panel.
 */
export interface DefaultFilterConfig {
  /** The grid column it filters. It must be a column (MUI drops an item whose column is missing)
   *  whose filter operators include "is" (a singleSelect column's do; give another column
   *  `filterOperators` that do). */
  field: string;
  /** The placeholder and accessible name ("Person responsible"). */
  label: string;
  /** The tenant's values for a select. Empty with no `renderField`: the field is not drawn. */
  options?: ListStatusOption[];
  /** The caller's own input (a server-searched user lookup): value as a string, "" = cleared. */
  renderField?: (value: string, onChange: (value: string) => void) => React.ReactNode;
}

export const STATUS_FILTER_FIELD = "status";
const STATUS_FILTER_ID = "status-filter";

type FilterItem = { id?: number | string; field: string; operator: string; value?: any };
type FilterModel = { items: FilterItem[]; logicOperator?: any; [k: string]: any };

/** The status values a model holds, as strings (empty = no status filter); `field` for a chip
 *  field that is not the status column. */
export function statusFilterValues(model: FilterModel | undefined, field: string = STATUS_FILTER_FIELD): string[] {
  const item = (model?.items ?? []).find((i) => i.field === field);
  if (!item || item.value == null || item.value === "") return [];
  return (Array.isArray(item.value) ? item.value : [item.value])
    .filter((v: unknown) => v != null && String(v) !== "")
    .map(String);
}

/** The value a model holds for a Default field, as a string ("" = no filter on it). */
export function defaultFilterValue(model: FilterModel | undefined, field: string): string {
  const item = (model?.items ?? []).find((i) => i.field === field);
  return item?.value == null ? "" : String(item.value);
}

/** The model with the Default field `config.field` set to "is" `value` ("" clears it); the
 *  option's own value (a number stays a number), every other item kept. */
export function withDefaultFilter(
  model: FilterModel,
  config: DefaultFilterConfig,
  value: string
): { model: FilterModel; reason: "upsertFilterItem" | "deleteFilterItem" } {
  const existing = model.items.find((i) => i.field === config.field);
  const rest = model.items.filter((i) => i.field !== config.field);
  if (!value) return { model: { ...model, items: rest }, reason: "deleteFilterItem" };
  const picked = config.options?.find((o) => String(o.value) === value)?.value ?? value;
  const id = existing?.id ?? freeId(`default-${config.field}`, model.items);
  return {
    model: { ...model, items: [...rest, { id, field: config.field, operator: "is", value: picked }] },
    reason: "upsertFilterItem",
  };
}

function freeId(base: string, items: FilterItem[]) {
  const ids = new Set(items.map((i) => String(i.id)));
  let id = base;
  for (let n = 0; ids.has(id); n += 1) id = `${base}-${n}`;
  return id;
}

/**
 * The model with its status filter set to `values` (strings; empty clears it): every other item,
 * the logic and the quick-search values kept. "is" takes the option's own value (the Tools route's
 * single-status query); "is any of" Demand's list of strings. With the reason MUI gives that edit.
 */
export function withStatusFilter(
  model: FilterModel,
  values: string[],
  config: Pick<StatusFilterConfig, "options" | "multiple" | "field">
): { model: FilterModel; reason: "upsertFilterItem" | "deleteFilterItem" } {
  const field = config.field ?? STATUS_FILTER_FIELD;
  const existing = model.items.find((i) => i.field === field);
  const rest = model.items.filter((i) => i.field !== field);
  const picked = config.multiple
    ? values
    : values.slice(-1).filter((v) => config.options.some((o) => String(o.value) === v));
  if (!picked.length) return { model: { ...model, items: rest }, reason: "deleteFilterItem" };
  const id = existing?.id ?? freeId(field === STATUS_FILTER_FIELD ? STATUS_FILTER_ID : `${field}-filter`, model.items);
  const item: FilterItem = config.multiple
    ? { id, field, operator: "isAnyOf", value: picked }
    : {
        id,
        field,
        operator: "is",
        value: config.options.find((o) => String(o.value) === picked[0])!.value,
      };
  return { model: { ...model, items: [...rest, item] }, reason: "upsertFilterItem" };
}

/** Width of the panel (Component 73), capped to the viewport on a phone. */
export const FILTER_PANEL_WIDTH = 690;

const SMALL_OUTLINED = { variant: "outlined", size: "small" } as const;

/** One Default Filters field's width (Status, and each `defaultFilters` field). */
const DEFAULT_FIELD_WIDTH = 200;

/** A section heading ("Default Filters", "Custom Filters"). */
const sectionTitleSx = {
  fontFamily: '"Roboto-Bold", "Roboto", sans-serif',
  fontSize: "15px",
  color: "common.black",
};

/**
 * The panel's look, in plain CSS (no sx shorthands: the theme's styleOverrides take it
 * verbatim). Set on the panel's root (`.MuiDataGrid-panelWrapper`).
 */
export const FILTER_PANEL_SX: Record<string, any> = {
  width: `${FILTER_PANEL_WIDTH}px`,
  maxWidth: "calc(100vw - 32px)",
  boxSizing: "border-box",
  padding: "24px",
  backgroundColor: DROPDOWN_MENU_BG,
  "& .MuiDataGrid-filterForm": {
    padding: "0",
    marginBottom: "12px",
    alignItems: "center",
    gap: "12px",
    flexWrap: "nowrap",
    backgroundColor: "transparent",
  },
  "@media (max-width: 599.95px)": {
    "& .MuiDataGrid-filterForm": { flexWrap: "wrap" },
  },
  "& .MuiDataGrid-filterFormDeleteIcon": { order: 4, width: "auto", marginLeft: "auto" },
  "& .MuiDataGrid-filterFormColumnInput, & .MuiDataGrid-filterFormOperatorInput, & .MuiDataGrid-filterFormValueInput":
    { width: "180px", flexShrink: 0, maxWidth: "100%" },
  "& .MuiDataGrid-filterForm .MuiFormControl-root": { margin: "0" },
  "& .MuiDataGrid-filterForm .MuiInputBase-root": { fontSize: "14px" },
  "& .MuiDataGrid-panelFooter": { padding: "0", backgroundColor: "transparent" },
};

/** The panel's popper paper: Component 73's 12px corner and the dropdown shadow. */
export const FILTER_PANEL_PAPER_SX: Record<string, any> = {
  borderRadius: "12px",
  boxShadow: DROPDOWN_MENU_SHADOW,
  backgroundColor: DROPDOWN_MENU_BG,
  maxHeight: "none",
};

/**
 * The theme's `MuiDataGrid` `styleOverrides.panelWrapper` / `.paper`: the look, on a panel only
 * while it holds a filter form (the Columns panel shares both and keeps its own). It reaches a
 * stock panel too — a fallback only: every grid that filters sets the shared slot, and
 * FilterPanelConformance.test.tsx fails one that does not.
 */
export const FILTER_PANEL_WRAPPER_OVERRIDE = {
  "&:has(.MuiDataGrid-filterForm)": FILTER_PANEL_SX,
};
export const FILTER_PANEL_PAPER_OVERRIDE = {
  "&:has(.MuiDataGrid-filterForm)": FILTER_PANEL_PAPER_SX,
};

/**
 * `slotProps.filterPanel` defaults for every grid: AND only, columns A–Z. A wrapper grid adds
 * its `statusFilter`; a consumer's own slot props go on top.
 */
export const FILTER_PANEL_PROPS: Record<string, any> = {
  logicOperators: ["and"],
  columnsSort: "asc",
};

/** What a grid's `slotProps.filterPanel` may hand the panel. */
export interface FilterPanelSlotProps {
  logicOperators?: string[];
  columnsSort?: "asc" | "desc";
  statusFilter?: StatusFilterConfig;
  /** More Default Filters fields, after Status. */
  defaultFilters?: DefaultFilterConfig[];
  /** MUI's own options, still honoured. */
  getColumnForNewFilter?: (args: { currentFilters: FilterItem[]; columns: any[] }) => string | null;
  disableAddFilterButton?: boolean;
  disableRemoveAllButton?: boolean;
  sx?: any;
  [k: string]: any;
}

/** The grid a wrapper hands the body, read from its own major's context. */
export interface FilterPanelGrid {
  /** The grid's apiRef (Pro 6.9 or Premium 7.1: the methods used are named alike). */
  apiRef: React.MutableRefObject<any>;
  model: FilterModel;
  /** The filterable column definitions (gridFilterableColumnDefinitionsSelector). */
  filterableColumns: Array<{ field: string; filterOperators?: Array<{ value: string }> }>;
  /** `!rootProps.disableMultipleColumnsFiltering`. */
  multipleColumns: boolean;
  /** That grid's GridFilterForm and GridPanelWrapper. */
  FilterForm: React.ComponentType<any>;
  PanelWrapper: React.ComponentType<any>;
  /** Premium 7.1: an item on a column that is not filterable is drawn read-only (as MUI 7 does). */
  readOnlySupported?: boolean;
}

type ColumnLike = FilterPanelGrid["filterableColumns"][number];

function newItem(column: ColumnLike, items: FilterItem[]): FilterItem {
  return {
    field: column.field,
    operator: column.filterOperators?.[0]?.value ?? "contains",
    id: freeId("filter", items),
  };
}

function QuickFiltersSwitch({ config }: { config: StatusFilterConfig }) {
  const [show, setShow] = useShowQuickFilters(
    config.quickFiltersKey ?? "quick-filters",
    config.quickFiltersDefault ?? true
  );
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
      <FormControlLabel
        sx={{ m: 0, gap: 1 }}
        label="Show Quick Filters"
        control={
          <Switch
            checked={show}
            onChange={(_, checked) => setShow(checked)}
            inputProps={{ "aria-label": "Show Quick Filters" }}
          />
        }
      />
    </Box>
  );
}

/** The panel itself (Component 73). Rendered by the two shared wrappers only. */
export const FilterPanelBody = React.forwardRef<HTMLDivElement, FilterPanelSlotProps & { grid: FilterPanelGrid }>(
  function FilterPanelBody(props, ref) {
    const {
      grid,
      logicOperators = FILTER_PANEL_PROPS.logicOperators,
      columnsSort = FILTER_PANEL_PROPS.columnsSort,
      statusFilter,
      defaultFilters,
      getColumnForNewFilter,
      disableAddFilterButton = false,
      disableRemoveAllButton = false,
      // the stock panel's form props: Component 73 owns what they set
      filterFormProps: _filterFormProps,
      children: _children,
      sx,
      ...other
    } = props;
    const { apiRef, model, filterableColumns, multipleColumns, FilterForm, PanelWrapper, readOnlySupported } = grid;
    const status = statusFilter?.options?.length ? statusFilter : undefined;
    const statusField = status?.field ?? STATUS_FILTER_FIELD;
    const statusLabel = status?.label ?? "Status";
    const extraDefaults = (defaultFilters ?? []).filter((d) => d.renderField || d.options?.length);
    // The Default section owns these fields; Custom never lists or offers them.
    const reservedKey = [...(status ? [statusField] : []), ...extraDefaults.map((d) => d.field)].join("|");
    const reserved = useMemo(() => new Set(reservedKey.split("|").filter(Boolean)), [reservedKey]);
    const hasDefaults = Boolean(status) || extraDefaults.length > 0;
    const lastFilterRef = useRef<any>(null);
    const placeholder = useRef<FilterItem | null>(null);

    const filterable = new Set(filterableColumns.map((c) => c.field));
    // the Default section owns its items; Custom lists the rest
    const custom = model.items.filter((i) => !reserved.has(i.field));
    const readOnlyItems = readOnlySupported ? custom.filter((i) => !filterable.has(i.field)) : [];
    const editable = readOnlySupported ? custom.filter((i) => filterable.has(i.field)) : custom;

    const columnFor = useCallback(
      (current: FilterItem[]): ColumnLike | null => {
        const candidates = filterableColumns.filter((c) => c.filterOperators?.length && !reserved.has(c.field));
        if (getColumnForNewFilter) {
          const field = getColumnForNewFilter({ currentFilters: current, columns: candidates });
          return field === null ? null : candidates.find((c) => c.field === field) ?? null;
        }
        return candidates[0] ?? null;
      },
      [filterableColumns, getColumnForNewFilter, reserved]
    );

    // An empty panel offers one row to fill (MUI's placeholder: not in the model until edited).
    if (editable.length) placeholder.current = null;
    else if (!placeholder.current) {
      const col = columnFor([]);
      placeholder.current = col ? newItem(col, model.items) : null;
    }
    const rows = editable.length ? editable : placeholder.current ? [placeholder.current] : [];

    // AND only: a model saved with OR goes back to the first allowed logic (MUI's own rule).
    useEffect(() => {
      if (logicOperators.length && model.logicOperator && !logicOperators.includes(model.logicOperator)) {
        apiRef.current.setFilterLogicOperator(logicOperators[0]);
      }
    }, [apiRef, logicOperators, model.logicOperator]);
    useEffect(() => {
      if (rows.length) lastFilterRef.current?.focus?.();
    }, [rows.length]);

    const applyFilter = useCallback((item: FilterItem) => apiRef.current.upsertFilterItem(item), [apiRef]);
    const deleteFilter = useCallback(
      (item: FilterItem) => {
        // MUI closes the panel with its last row; with a Default section there is more to it
        const closing = rows.length === 1 && !hasDefaults;
        apiRef.current.deleteFilterItem(item);
        if (closing) apiRef.current.hideFilterPanel();
      },
      [apiRef, rows.length, hasDefaults]
    );
    const addCriteria = () => {
      const col = columnFor(rows);
      if (!col) return;
      const kept = model.items.filter((i) => !rows.includes(i));
      apiRef.current.upsertFilterItems([...kept, ...rows, newItem(col, [...model.items, ...rows])]);
    };
    const removeAll = () => apiRef.current.setFilterModel({ ...model, items: [] }, "removeAllFilterItems");
    const applyStatus = (values: string[]) => {
      const next = withStatusFilter(model, values, status!);
      apiRef.current.setFilterModel(next.model, next.reason);
    };
    const statusValues = statusFilterValues(model, statusField);
    const applyDefault = (config: DefaultFilterConfig, value: string) => {
      const next = withDefaultFilter(model, config, value);
      apiRef.current.setFilterModel(next.model, next.reason);
    };

    const formProps = {
      hasMultipleFilters: false, // AND only: no logic column
      logicOperators,
      columnsSort,
      // a Custom row never offers a field the Default section owns (its own field kept)
      filterColumns: ({ field, columns }: { field: string; columns: Array<{ field: string }> }) =>
        columns.filter((c) => c.field === field || !reserved.has(c.field)).map((c) => c.field),
      columnInputProps: SMALL_OUTLINED,
      operatorInputProps: SMALL_OUTLINED,
      valueInputProps: { ...SMALL_OUTLINED, InputComponentProps: SMALL_OUTLINED },
      multiFilterOperator: model.logicOperator ?? "and",
      applyMultiFilterOperatorChanges: (op: string) => apiRef.current.setFilterLogicOperator(op),
      applyFilterChanges: applyFilter,
      deleteFilter,
    };

    return (
      <PanelWrapper
        ref={ref}
        {...other}
        data-testid="filter-panel"
        role="dialog"
        aria-label="Filters"
        sx={[FILTER_PANEL_SX, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      >
        {status && <QuickFiltersSwitch config={status} />}
        {hasDefaults && (
          <>
            <Typography sx={{ ...sectionTitleSx, mb: 1 }}>Default Filters</Typography>
            <Divider sx={{ mb: 1.75 }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
            {status && (
            <TextField
              select
              value={status.multiple ? statusValues : statusValues[0] ?? ""}
              onChange={(e) => {
                const v = e.target.value as unknown;
                applyStatus(Array.isArray(v) ? v.map(String).filter(Boolean) : v ? [String(v)] : []);
              }}
              SelectProps={{
                displayEmpty: true,
                multiple: Boolean(status.multiple),
                renderValue: (v: unknown) => {
                  const picked = (Array.isArray(v) ? v : v ? [v] : []).map(String);
                  if (!picked.length) return statusLabel;
                  return picked.map((p) => status.options.find((o) => String(o.value) === p)?.label ?? p).join(", ");
                },
              }}
              inputProps={{ "aria-label": `${statusLabel} filter` }}
              size="small"
              sx={{ width: DEFAULT_FIELD_WIDTH, maxWidth: "100%", mt: 0 }}
            >
              {!status.multiple && <MenuItem value="">{statusLabel}</MenuItem>}
              {status.options.map((o) => (
                <MenuItem key={String(o.value)} value={String(o.value)}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            )}
            {extraDefaults.map((d) => {
              const value = defaultFilterValue(model, d.field);
              const onChange = (v: string) => applyDefault(d, v);
              return (
                <Box key={d.field} sx={{ width: DEFAULT_FIELD_WIDTH, maxWidth: "100%" }}>
                  {d.renderField ? (
                    d.renderField(value, onChange)
                  ) : (
                    <TextField
                      select
                      fullWidth
                      value={value}
                      onChange={(e) => onChange(String(e.target.value ?? ""))}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (v: unknown) =>
                          v ? d.options!.find((o) => String(o.value) === String(v))?.label ?? String(v) : d.label,
                      }}
                      inputProps={{ "aria-label": `${d.label} filter` }}
                      size="small"
                    >
                      <MenuItem value="">{d.label}</MenuItem>
                      {d.options!.map((o) => (
                        <MenuItem key={String(o.value)} value={String(o.value)}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
              );
            })}
            </Box>
          </>
        )}
        <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Custom Filters</Typography>
        <Divider sx={{ mb: 3 }} />
        <Box className="MuiDataGrid-panelContent">
          {readOnlyItems.map((item) => (
            <FilterForm key={String(item.id)} item={item} {...formProps} readOnly focusElementRef={null} />
          ))}
          {rows.map((item, index) => (
            <FilterForm
              key={item.id == null ? index : String(item.id)}
              item={item}
              {...formProps}
              focusElementRef={index === rows.length - 1 ? lastFilterRef : null}
            />
          ))}
        </Box>
        {multipleColumns && !(disableAddFilterButton && disableRemoveAllButton) && (
          <Box className="MuiDataGrid-panelFooter" sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
            {!disableAddFilterButton ? (
              <Button startIcon={<AddIcon />} disabled={!columnFor(rows)} onClick={addCriteria} sx={{ p: 0, fontSize: "13px" }}>
                Add Criteria
              </Button>
            ) : (
              <span />
            )}
            {!disableRemoveAllButton && (
              <Button startIcon={<DeleteIcon />} onClick={removeAll} sx={{ p: 0, fontSize: "13px" }}>
                Remove All
              </Button>
            )}
          </Box>
        )}
      </PanelWrapper>
    );
  }
);
